<template>
  <sidebar-comp
    :user="user"
    :title="routeName"
    :menuItems="menuItems.menuItems"
  ></sidebar-comp>
  <v-main>
    <v-container fluid>
      <slot />
    </v-container>
    <footer-comp></footer-comp>
  </v-main>
</template>
<script>
import { mapState, mapActions } from "pinia";
import { useUserStore } from "../../stores/UserStore";
import SidebarComp from "./SidebarComp.vue";
import FooterComp from "./FooterComp.vue";
import { hasPageAuth } from "@/utils/pageAuth";

// Her sayfa kendi <BaseLayoutComp>'unu oluşturduğu için sayfa geçişinde menü baştan kurulup
// yetkiler yeniden çekiliyordu (menü boş görünüp yeniden doluyordu). Kurulan menü burada
// kullanıcı + dile göre kısa süre hatırlanır.
const MENU_CACHE_TTL_MS = 5 * 60 * 1000;
let menuCache = null; // { key, items, builtAt }
export default {
  components: {
    SidebarComp,
    FooterComp,
  },
  computed: {
    ...mapState(useUserStore, ["getUserInfo", "getAllUserAuth"]),
  },
  async created() {
    const cacheKey = `${this.$i18n?.locale}:${this.getUserInfo?.name}:${this.getUserInfo?.surname}:${this.getUserInfo?.role}`;
    this.user.fullName = this.getUserInfo.name + " " + this.getUserInfo.surname;
    if (
      menuCache &&
      menuCache.key === cacheKey &&
      Date.now() - menuCache.builtAt < MENU_CACHE_TTL_MS
    ) {
      this.menuItems.menuItems = menuCache.items;
      return;
    }
    // Menü konuya göre gruplanmıştır. Yetki süzmesi öğe bazlıdır: yetkisi olmayan alt öğe listelenmez,
    // hiçbir alt öğesine yetkisi olmayan grup tamamen gizlenir.
    const menuArray = [
      // ── Sık kullanılanlar (tek tık) ──
      { title: this.$t("araclar"), icon: "mdi-taxi", route: "/#/vehicles" },
      {
        title: this.$t("suruculer"),
        icon: "mdi-card-account-details",
        route: "/#/drivers",
      },
      {
        title: this.$t("arac_sahibi"),
        icon: "mdi-account-circle",
        route: "/#/owners",
      },
      {
        title: "Filo Takip",
        icon: "mdi-car-multiple",
        route: "/#/owner_fleet_map",
      },
      {
        title: "Bildirimlerim",
        icon: "mdi-bell-outline",
        route: "/#/notifications",
      },
      {
        title: "Uygulama Merkezi",
        icon: "mdi-apps",
        route: "/#/apps",
      },
      // ── Yönetim ──
      {
        title: this.$t("yonetim_paneli"),
        icon: "mdi-cogs",
        route: "#",
        child: [
          {
            title: this.$t("rol_yonetimi"),
            icon: "mdi-account",
            route: "/#/roles",
          },
          {
            title: this.$t("kullanici_yonetimi"),
            icon: "mdi-account",
            route: "/#/users",
          },
          {
            title: this.$t("sayfa_yonetimi"),
            icon: "mdi-account",
            route: "/#/pages",
          },
          {
            title: "Ayarlar",
            icon: "mdi-cogs",
            route: "/#/settings",
          },
          {
            title: "Bildirim İzleme",
            icon: "mdi-bell-ring-outline",
            route: "/#/admin_notifications",
          },
          {
            title: "Panik Butonu Uyarıları",
            icon: "mdi-alarm-light-outline",
            route: "/#/panic_alerts",
          },
          {
            title: this.$t("log_gecmisi"),
            icon: "mdi-clock-fast",
            route: "/#/process_logs",
          },
        ],
      },
      {
        title: "Araç ve Cihaz Yönetimi",
        icon: "mdi-car-cog",
        route: "#",
        child: [
          {
            title: "Araç Listeleme Sayfası (Yönetici)",
            icon: "mdi-list-box-outline",
            route: "/#/admin_vehicle_list",
          },
          {
            title: "Cihaz Yönetim Sistemi",
            icon: "mdi-developer-board",
            route: "/#/firmwares/device-management",
          },
          {
            title: "Cihaz Durumları",
            icon: "mdi-access-point-network",
            route: "/#/device_statuses",
          },
          {
            title: this.$t("arac_yolculuk_fotograflari"),
            icon: "mdi-camera",
            route: "/#/vehicle_journey_photos",
          },
          {
            title: "Yolculuk Fot. Ayarları",
            icon: "mdi-camera-cog",
            route: "/#/vehicle_journey_photos_settings",
          },
          {
            title: this.$t("onay_bekleyen_surucu_fotograflari"),
            icon: "mdi-account-circle",
            route: "/#/pending_approval_images",
          },
          {
            title: "Sürücü Şablonları",
            icon: "mdi-card-text-outline",
            route: "/#/driver_templates",
          },
          {
            title: "QR Kod Yönetimi",
            icon: "mdi-qrcode",
            route: "/#/qr_codes/list",
          },
          {
            title: "Reklam Alanı Listeleme Sayfası",
            icon: "mdi-list-box-outline",
            route: "/#/adzones",
          },
        ],
      },
      // ── Finans ──
      {
        title: "Muhasebe ve Abonelik",
        icon: "mdi-account-credit-card-outline",
        route: "#",
        child: [
          {
            title: "Abonelik Listesi",
            icon: "mdi-account",
            route: "/#/subscriptions",
          },
          {
            title: "Ödeme / Abonelik Raporları",
            icon: "mdi-chart-bar",
            route: "/#/paytr_reports",
          },
          {
            title: "Plan Listesi",
            icon: "mdi-account",
            route: "/#/admin_plans",
          },
          {
            title: "Bölge Tarifeleri Listesi",
            icon: "mdi-account",
            route: "/#/region_tariffs",
          },
          {
            title: "Araç Raporları (Muhasebe)",
            icon: "mdi-list-box-outline",
            route: "/#/report_page",
          },
          {
            title: this.$t("fatura_bilgisi"),
            icon: "mdi-receipt-text-outline",
            route: "/#/invoice_info",
          },
          {
            title: this.$t("siparis_gecmisi"),
            icon: "mdi-list-box-outline",
            route: "/#/payment_histories",
          },
          {
            title: this.$t("satin_al"),
            icon: "mdi-playlist-plus",
            route: "/#/plans",
          },
        ],
      },
      // ── Bayi ──
      {
        title: "Bayi ve Duraklar",
        icon: "mdi-store-outline",
        route: "#",
        child: [
          {
            title: this.$t("bayi_dashboard"),
            icon: "mdi-store-outline",
            route: "/#/dealer/dashboard",
          },
          {
            title: this.$t("bayi_duraklari_listesi"),
            icon: "mdi-store-marker-outline",
            route: "/#/dealer_stations",
          },
          {
            title: this.$t("bayiler"),
            icon: "mdi-store-outline",
            route: "/#/dealers",
          },
          {
            title: this.$t("duraklar"),
            icon: "mdi-store-24-hour",
            route: "/#/stations",
          },
        ],
      },
      // ── Tarife / Geçiş ──
      {
        title: this.$t("tarife_servisi"),
        icon: "mdi-cogs",
        route: "#",
        child: [
          {
            title: "Tarife Listesi",
            icon: "mdi-list-box-outline",
            route: "/#/tariffs",
          },
          {
            title: this.$t("tarife_gecmisi_listeleme"),
            icon: "mdi-list-box-outline",
            route: "/#/tariff_histories",
          },
          {
            title: this.$t("tarife_tanimlama"),
            icon: "mdi-playlist-plus",
            route: "/#/tariff_match",
          },
          {
            title: "Tarife Raporu",
            icon: "mdi-file-chart-outline",
            route: "/#/tariff_report",
          },
          {
            title: "WinForm Logları",
            icon: "mdi-text-box-search-outline",
            route: "/#/winform_logs",
          },
        ],
      },
      {
        title: "Ücretli Geçiş Servisi",
        icon: "mdi-boom-gate-outline",
        route: "#",
        child: [
          {
            title: "Ücretli Geçiş Kayıtları Listeleme Sayfası",
            icon: "mdi-boom-gate-up-outline",
            route: "/#/toll_histories",
          },
          {
            title: "Ücretli Geçiş Noktaları Listeleme Sayfası",
            icon: "mdi-map-marker-radius-outline",
            route: "/#/toll_segments",
          },
          {
            title: "Ücretli Geçiş Noktası Ekle",
            icon: "mdi-map-marker-plus-outline",
            route: "/#/add_toll",
          },
          {
            title: "Ücretli Geçiş Noktası Fiyat Düzenleme",
            icon: "mdi-map-marker-plus-outline",
            route: "/#/toll_segments_edit_price",
          },
        ],
      },
      // ── MDM ──
      {
        title: "MDM API Servisi",
        icon: "mdi-devices",
        route: "#",
        child: [
          {
            title: "MDM Araç Oluşturma Sayfası (Muhasebe)",
            icon: "mdi-list-box-outline",
            route: "/#/mdm_create_vehicle",
          },
          {
            title: "MDM Konfigürasyon Listeleme ve Tanımlama Sayfası",
            icon: "mdi-list-box-outline",
            route: "/#/mdm_configs",
          },
          {
            title: "MDM Hızlı Araç Oluşturma",
            icon: "mdi-car-cog",
            route: "/#/mdm/create_with_config",
          },
          {
            title: "Konfigürasyonlar",
            icon: "mdi-list-box-outline",
            route: "/#/mdm/configurations",
          },
          {
            title: "Cihazlar",
            icon: "mdi-list-box-outline",
            route: "/#/mdm/devices",
          },
          {
            title: "Uygulamalar",
            icon: "mdi-list-box-outline",
            route: "/#/mdm/applications",
          },
          {
            title: "Toplu Cihaz Konfigürasyon Güncelleme",
            icon: "mdi-list-box-outline",
            route: "/#/mdm/multiple_device_update",
          },
        ],
      },
      // ── Envanter ──
      {
        title: this.$t("envanter"),
        icon: "mdi-cogs",
        route: "#",
        child: [
          {
            title: this.$t("envanter_olcu_birimi_listesi"),
            icon: "mdi-list-box-outline",
            route: "/#/inventory_measurements",
          },
          {
            title: this.$t("envanter_durumu_listesi"),
            icon: "mdi-list-box-outline",
            route: "/#/inventory_status",
          },
          {
            title: this.$t("envanter_urun_turu_listesi"),
            icon: "mdi-list-box-outline",
            route: "/#/inventory_products",
          },
          {
            title: this.$t("envanter_marka_listesi"),
            icon: "mdi-list-box-outline",
            route: "/#/inventory_brands",
          },
          {
            title: this.$t("envanter_stok_karti_listesi"),
            icon: "mdi-list-box-outline",
            route: "/#/inventory_card_stocks",
          },
          {
            title: this.$t("envanter_listesi"),
            icon: "mdi-list-box-outline",
            route: "/#/inventories",
          },
          {
            title: "Toplu Envanter Ekle",
            icon: "mdi-file-plus-outline",
            route: "/#/inventory/bulk-add",
          },
        ],
      },
      // ── Servis ──
      {
        title: this.$t("servis_islemleri"),
        icon: "mdi-wrench-outline",
        route: "#",
        child: [
          {
            title: this.$t("arac_islemleri"),
            icon: "mdi-list-box-outline",
            route: "/#/services/new_service_record",
          },
          {
            title: this.$t("bekleyen_servis_islemleri"),
            icon: "mdi-list-box-outline",
            route: "/#/services/waiting_processes",
          },
          {
            title: this.$t("tamamlanan_servis_islemleri"),
            icon: "mdi-list-box-outline",
            route: "/#/services/completed_processes",
          },
          {
            title: this.$t("arac_islemleri") + " (MERKEZ)",
            icon: "mdi-list-box-outline",
            route: "/#/main_services/new_service_record",
          },
          {
            title: this.$t("bekleyen_servis_islemleri") + " (MERKEZ)",
            icon: "mdi-list-box-outline",
            route: "/#/main_services/waiting_processes",
          },
          {
            title: this.$t("tamamlanan_servis_islemleri") + " (MERKEZ)",
            icon: "mdi-list-box-outline",
            route: "/#/main_services/completed_processes",
          },
        ],
      },
      {
        title: "Servis Arıza Form İşlemleri",
        icon: "mdi-clipboard-text-outline",
        route: "#",
        child: [
          {
            title: "Arıza Formu Oluştur",
            icon: "mdi-plus-box-outline",
            route: "/#/service_faults/add",
          },
          {
            title: "Geçmiş Arıza Formları",
            icon: "mdi-format-list-bulleted",
            route: "/#/service_faults/list",
          },
          {
            title: "Servis Arıza Ayarları",
            icon: "mdi-source-branch",
            route: "/#/service_faults/settings",
          },
        ],
      },
    ];

    this.user.fullName = this.getUserInfo.name + " " + this.getUserInfo.surname;
    await this.getAllUserAuthsAction();
    for (const i of menuArray) {
      if (i.child) {
        const childArray = [];
        for (const y of i.child) {
          const response = this.getAllUserAuth.find((t) => {
            if (!t || !t.page || !t.page.path) return false;
            const dbPath = t.page.path.startsWith("/")
              ? t.page.path.slice(1)
              : t.page.path;
            let menuPath = y.route.slice(2).startsWith("/")
              ? y.route.slice(3)
              : y.route.slice(2);

            if (dbPath.toLowerCase() === menuPath.toLowerCase()) return true;
            return false;
          });

          if (response) {
            if (response?.permissions?.permissionName !== "Unauthorized") {
              childArray.push(y);
            }
          }
        }
        if (childArray.length > 0) {
          this.menuItems.menuItems.push(i);
          i.child = childArray;
        }
      } else {
        const response = this.getAllUserAuth.find((t) => {
          if (!t || !t.page || !t.page.path) return false;
          const dbPath = t.page.path.startsWith("/")
            ? t.page.path.slice(1)
            : t.page.path;
          const menuPath = i.route.slice(2).startsWith("/")
            ? i.route.slice(3)
            : i.route.slice(2);
          if (dbPath.toLowerCase() === menuPath.toLowerCase()) return true;
          return false;
        });

        const menuPath = i.route.slice(2).startsWith("/")
          ? i.route.slice(3)
          : i.route.slice(2);
        const bypassRoutes = [
          "firmwares/configs",
          "firmwares/assignment",
          "firmwares/list",
        ];
        const isBypassed = bypassRoutes.some((br) =>
          menuPath.toLowerCase().includes(br),
        );

        if (response || isBypassed) {
          if (
            isBypassed ||
            response?.permissions.permissionName !== "Unauthorized"
          ) {
            this.menuItems.menuItems.push(i);
          }
        }
      }
    }

    // Micro Yönetim: "Sayfa Yönetimi"nde tanımlı sayfa yetkisine göre gösterilir (rol ya da kullanıcıya özel izin).
    // Yetkisi olmayan alt sayfalar listelenmez; hiçbirine yetkisi yoksa grup tamamen gizlenir.
    {
      const microMenu = {
        title: "Micro Yönetim",
        icon: "mdi-api",
        route: "#",
        child: [
          {
            title: "API Anahtarları",
            icon: "mdi-key-variant",
            route: "/#/micro/keys",
          },
          {
            title: "Tarife İstekleri",
            icon: "mdi-format-list-checks",
            route: "/#/micro/tariff_requests",
          },
          {
            title: "Micro Logları",
            icon: "mdi-text-box-search-outline",
            route: "/#/micro/logs",
          },
        ],
      };
      microMenu.child = microMenu.child.filter((c) => hasPageAuth(c.route));
      if (microMenu.child.length > 0) {
        // "Yönetim Paneli" grubunun hemen altına, yoksa en sona eklenir.
        const adminIndex = this.menuItems.menuItems.findIndex(
          (m) => m.title === this.$t("yonetim_paneli"),
        );
        this.menuItems.menuItems.splice(
          adminIndex >= 0 ? adminIndex + 1 : this.menuItems.menuItems.length,
          0,
          microMenu,
        );
      }
    }

    menuCache = {
      key: cacheKey,
      items: this.menuItems.menuItems,
      builtAt: Date.now(),
    };

    // Harita Ekleme (Araç Sahibi)
    /*
    const role = (this.getUserInfo?.role || "").toString().toLowerCase().trim();
    const isVehicleOwner = role === "araç sahibi" || role === "arac sahibi" || role.includes("sahib") || role === "owner" || role.includes("owner") || role === "vehicleowner";
    
    if (isVehicleOwner) {
      this.menuItems.menuItems.push({
        title: "Araçlarım (Harita)",
        icon: "mdi-map",
        route: "/#/owner_map"
      });
    }
    */
  },
  methods: {
    ...mapActions(useUserStore, ["getAllUserAuthsAction"]),
  },
  data() {
    return {
      routeName: this.$route.name,
      user: {
        fullName: null,
      },
      menuItems: {
        menuItems: [],
      },
    };
  },
};
</script>
<style>
body {
  font-family: "Source Sans Pro", -apple-system, BlinkMacSystemFont, "Segoe UI",
    Roboto, "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji",
    "Segoe UI Emoji", "Segoe UI Symbol";
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
}
.v-main {
  background-color: #e9ecef;
  min-height: 87vh !important;
}
.v-container {
  min-height: 88vh;
}
</style>
